import { useEffect, useRef, type ReactNode } from "react";

import { CopyButton } from "@/components/copy-button";
import { highlight } from "@/lib/highlight";
import { cn } from "@/lib/utils";

/**
 * A file that grows as the reader steps through a chapter.
 *
 * The file is written once, as chunks in file order, each tagged with the step that adds it
 * and, if a later step replaces it, the step that removes it. At step `i` the panel shows
 * every chunk alive at `i`, and the ones that arrived at `i` are marked -- a bar down the left
 * and a tint -- so the reader sees what this step did to the file, the way a tutorial's code
 * listing highlights the new lines. A file taller than the panel scrolls inside it, and each
 * step scrolls its first new line into view, so the point of a step is never below the fold.
 * Lines are highlighted one at a time, which is right for shell and for the Python here;
 * nothing spans lines.
 */

export interface Chunk {
  /** The step this chunk appears at. */
  from: number;
  /** The step it disappears at, when a later step replaces it. */
  until?: number;
  code: string;
}

export function CodePanel({
  chunks,
  step,
  language,
  title,
  copy,
  action,
  className,
}: {
  chunks: Chunk[];
  step: number;
  language: string;
  /** The file name, or what the block is. */
  title: ReactNode;
  /** What the copy button gives, when it differs from the visible text. */
  copy?: (visible: string) => string;
  /** Sits before the copy button. */
  action?: ReactNode;
  className?: string;
}) {
  const alive = chunks.filter((c) => c.from <= step && (c.until === undefined || step < c.until));
  // A file that never grows has nothing "new" in it; only mark arrivals where there are steps.
  const grows = chunks.some((c) => c.from !== chunks[0].from);
  const lines = alive.flatMap((chunk) =>
    chunk.code.split("\n").map((text) => ({ text, fresh: grows && chunk.from === step })),
  );
  const visible = lines.map((l) => l.text).join("\n");
  const pre = useRef<HTMLPreElement>(null);
  useEffect(() => {
    const container = pre.current;
    const fresh = container?.querySelector<HTMLElement>("[data-fresh]");
    if (!container || !fresh || container.scrollHeight <= container.clientHeight) return;
    container.scrollTo({ top: Math.max(0, fresh.offsetTop - container.clientHeight * 0.35), behavior: "smooth" });
  }, [step]);
  return (
    <div className={cn("bg-muted min-w-0 rounded-lg border", className)}>
      <div className="flex items-center gap-3 border-b px-3 py-2">
        <span className="text-muted-foreground min-w-0 flex-1 truncate font-mono text-xs">{title}</span>
        {action}
        <CopyButton text={copy ? copy(visible) : visible} className="bg-background" />
      </div>
      <pre ref={pre} className="hljs relative max-h-[calc(100vh-14rem)] overflow-auto py-3 text-xs leading-relaxed">
        <code>
          {lines.map((line, i) => (
            <div
              // Index is the identity here: a line keeps its element as it moves from fresh
              // to settled, so only its colour changes, not its mount.
              key={i}
              data-fresh={line.fresh && (i === 0 || !lines[i - 1].fresh) ? "" : undefined}
              className={cn(
                "border-l-2 px-3 transition-colors duration-500",
                line.fresh ? "border-primary bg-primary/10" : "border-transparent",
              )}
            >
              {line.text === "" ? "\u00a0" : highlight(line.text, language)}
            </div>
          ))}
        </code>
      </pre>
    </div>
  );
}
